import { useTranslation } from "react-i18next";
import { Separator } from "@/src/lib/components/ui/separator";
import Icon from "../../lib/components/custom/Icon";
import { Image } from "../../lib/components/custom/Image";
import { Badge } from "../../lib/components/ui/badge";
import { Button } from "../../lib/components/ui/button";
import { Card } from "../../lib/components/ui/card";
import { calculateExtrasTotal, getExtraPrice } from "../../lib/constants";

interface CartItemCardProps {
	item: any;
	onEditSize: () => void;
	onEditExtras: () => void;
	onRequestRemove: () => void;
	onDecrease: () => void;
	onIncrease: () => void;
	isUpdating?: boolean;
}

export function CartItemCard({
	item,
	onEditSize,
	onEditExtras,
	onRequestRemove,
	onDecrease,
	onIncrease,
	isUpdating = false,
}: CartItemCardProps) {
	const { t } = useTranslation();
	const basePrice = parseFloat(item.menuSize.price);
	const extrasTotal = calculateExtrasTotal(item.extras, item.menuSize.sizeName);
	const itemTotal = (basePrice + extrasTotal) * item.quantity;
	const unitTotal = basePrice + extrasTotal;

	return (
		<Card className="p-3 sm:p-4 md:p-5 border-0 shadow-lg bg-white rounded-xl overflow-hidden transition-all duration-200 hover:shadow-xl">
			{/* Mobile image */}
			<div className="md:hidden mb-3">
				<div className="relative overflow-hidden mx-auto w-28">
					{item.menuItem.imageUrl ? (
						<Image
							src={item.menuItem.imageUrl}
							alt={item.menuItem.name}
							className="w-full h-full object-cover rounded-full aspect-square"
						/>
					) : (
						<div className="w-full h-full bg-accent flex items-center justify-center">
							<Icon name="Image" className="w-8 h-8 text-muted-foreground" />
						</div>
					)}
				</div>
			</div>

			<Separator className="my-3" />

			<div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
				{/* Desktop image */}
				<div className="shrink-0 hidden md:flex items-start gap-3">
					<div className="relative overflow-hidden rounded-xl">
						{item.menuItem.imageUrl ? (
							<Image
								src={item.menuItem.imageUrl}
								alt={item.menuItem.name}
								className="w-20 h-20 sm:w-24 sm:h-24 object-cover"
							/>
						) : (
							<div className="w-20 h-20 sm:w-24 sm:h-24 bg-accent flex items-center justify-center">
								<Icon name="Image" className="w-7 h-7 text-muted-foreground" />
							</div>
						)}
					</div>
				</div>

				<div className="flex-1 min-w-0 flex flex-col justify-between gap-2">
					<div>
						<div className="flex items-start justify-between gap-2">
							<div className="min-w-0 flex-1 mr-2">
								<h3 className="font-semibold text-sm sm:text-base md:text-lg text-foreground truncate">
									{item.menuItem.name}
								</h3>
								<div className="text-[11px] sm:text-xs text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
									<Badge
										variant="outline"
										className="bg-background/50 font-normal px-2 py-0.5 text-[11px]"
									>
										{item.menuSize.sizeName}
									</Badge>
									<span className="text-[10px] text-muted-foreground/60">
										•
									</span>
									<span className="text-[10px] capitalize">
										{item.menuItem.category}
									</span>
								</div>
							</div>

							<div className="flex gap-1">
								<Button
									variant="ghost"
									size="icon"
									onClick={onEditSize}
									className="h-7 w-7 rounded-full"
								>
									<Icon name="Pencil" className="w-3.5 h-3.5" />
								</Button>
								<Button
									variant="ghost"
									size="icon"
									onClick={onRequestRemove}
									className="h-7 w-7 rounded-full text-muted-foreground hover:text-destructive"
								>
									<Icon name="Trash2" className="w-3.5 h-3.5" />
								</Button>
							</div>
						</div>

						{item.extras && item.extras.length > 0 ? (
							<div className="mt-2">
								<div className="flex flex-wrap gap-1.5">
									{(item.extras as any[]).map((extra, idx: number) => {
										const extraPrice = getExtraPrice(
											extra,
											item.menuSize.sizeName,
										);
										return (
											<Badge
												key={extra.id ?? extra.menuExtraId ?? idx}
												variant="secondary"
												className="bg-primary/5 border-primary/10 px-2 py-0.5 text-[11px]"
											>
												<span className="mr-1 text-primary font-medium truncate max-w-[120px]">
													{extra.name}
												</span>
												{extraPrice > 0 && (
													<span className="text-[10px] bg-background/50 px-1 rounded-sm text-primary/80">
														+€{extraPrice.toFixed(2)}
													</span>
												)}
												{extra.quantity > 1 && (
													<span className="ml-1 text-[10px] text-muted-foreground font-mono">
														x{extra.quantity}
													</span>
												)}
											</Badge>
										);
									})}
								</div>
							</div>
						) : (
							<div className="mt-1.5">
								<Button
									variant="link"
									size="sm"
									className="h-auto p-0 text-[11px] text-muted-foreground hover:text-primary"
									onClick={onEditExtras}
								>
									<Icon name="Plus" className="w-3 h-3 mr-1" />
									{t("cart.item.add_extras")}
								</Button>
							</div>
						)}
					</div>

					<div className="flex items-end justify-between gap-3 mt-2">
						<div className="flex items-center gap-2 bg-muted/50 rounded-full px-2 py-1 border border-border/60 w-max">
							<Button
								variant="ghost"
								size="sm"
								onClick={onDecrease}
								disabled={isUpdating}
								className="w-7 h-7 rounded-full p-0 disabled:opacity-50"
							>
								<Icon name="Minus" className="w-3 h-3" />
							</Button>
							<span className="min-w-6 text-center font-medium text-xs sm:text-sm tabular-nums">
								{item.quantity}
							</span>
							<Button
								variant="ghost"
								size="sm"
								onClick={onIncrease}
								disabled={isUpdating}
								className="w-7 h-7 rounded-full p-0 disabled:opacity-50"
							>
								<Icon name="Plus" className="w-3 h-3" />
							</Button>
						</div>
						<div className="text-right">
							<div className="text-base sm:text-lg font-bold text-primary">
								€{itemTotal.toFixed(2)}
							</div>
							{item.quantity > 1 && (
								<div className="text-[11px] sm:text-xs text-muted-foreground">
									€{unitTotal.toFixed(2)} {t("cart.item.each")}
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
		</Card>
	);
}
